// src/components/DesignSystemDialog.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { electricalSaveError, electricalSaveFailure } from "./config/electricalSaveSnapshot";
import { useTheme } from "@mui/material/styles";
import PvLoadingRegion from "./pv/PvLoadingRegion";
import {
  Button,
  CircularProgress,
  Typography,
  Tooltip,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper,
  Box,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import {
  PanelShell,
  PanelShellActions,
  PanelShellContent,
  PanelShellTitle,
} from "./PanelShell";
import { useIsGreek } from "../context/LanguageContext";
import { PRIMARY_ID, displayName, withMpptOrientation } from "./config/orientations";
import { isDesignWorkflowNote, isElectricalContext, liveElectricalDesign } from "./config/liveElectricalDesign";
import { LiveDesignStatus, LiveMpptFeedback } from "./config/LiveElectricalFeedback";

/**
 * Build base MPPT state from meta.mppts or fallback layout counts.
 * meta.mppts is an array like [2, 2]  => 2 MPPTs, 2 inputs each.
 */
const connectedInput = (input) => typeof input?.used === "boolean" ? input.used
  : input?.modules_per_string != null || input?.modulesPerString != null;
const stringCountLabel = count => `${count} ${count === 1 ? "string" : "strings"}`;

function buildInitialMpptState(meta, fallbackLayout) {
  const mpptCounts = (() => {
    if (Array.isArray(meta?.mppts)) return meta.mppts;
    if (Array.isArray(meta)) return meta;
    if (Array.isArray(fallbackLayout)) {
      return fallbackLayout.map((mppt) =>
        Array.isArray(mppt?.inputs) ? mppt.inputs.length : 0
      );
    }
    return [];
  })();

  if (!mpptCounts.length) return [];

  return mpptCounts.map((inputsCount, idx) => ({
    mpptIndex: idx,
    modulesPerString: "",
    inputs: Array.from({ length: inputsCount }, () => ({
      used: false,
    })),
  }));
}

function buildLayoutFromInitialDesign(initialDesign, meta) {
  if (!initialDesign) {
    const base = buildInitialMpptState(meta, []);
    return { nInverters: 1, inverterOrder: ["INVERTER #1"], layout: [base] };
  }

  // Treat raw systemDesign object as layoutByInverter
  const layoutByInv =
    initialDesign.layoutByInverter ||
    (initialDesign.layout ? null : initialDesign);
  const savedLayoutRaw = initialDesign.layout;

  let inferredOrder =
    initialDesign.inverterOrder ||
    (layoutByInv ? Object.keys(layoutByInv) : null) ||
    (Array.isArray(savedLayoutRaw)
      ? savedLayoutRaw.map((_, idx) => `INVERTER #${idx + 1}`)
      : null) ||
    [];

  const layoutArray = layoutByInv
    ? inferredOrder.map((name) => layoutByInv?.[name] || [])
    : Array.isArray(savedLayoutRaw)
    ? savedLayoutRaw
    : [];

  if (!layoutArray.length) {
    const base = buildInitialMpptState(meta, []);
    return {
      nInverters: 1,
      inverterOrder: inferredOrder.length ? inferredOrder : ["INVERTER #1"],
      layout: [base],
    };
  }

  const normalizedLayout = layoutArray.map((mppts, invIdx) => {
    const metaMppts =
      meta?.byInverter?.[inferredOrder[invIdx]]?.mppts ||
      meta?.byInverter?.[invIdx]?.mppts ||
      meta?.mppts ||
      null;
    // Drop unused placeholders from the previous inverter. Keep connected
    // obsolete trackers visible so changing hardware never discards wiring.
    const lastConnected = (Array.isArray(mppts) ? mppts : []).reduce((last, mppt, index) =>
      (mppt?.inputs || []).some(connectedInput) ? index + 1 : last, 0);
    const totalMppts = Array.isArray(metaMppts)
      ? Math.max(metaMppts.length, lastConnected) : (mppts?.length || 0);
    const mpptList = Array.from({ length: totalMppts }, (_, mpptIdx) => mppts?.[mpptIdx] || {});

    return mpptList.map((mppt, mpptIdx) => {
      const inputsRaw = Array.isArray(mppt?.inputs) ? mppt.inputs : [];
      const maxInputIdx = inputsRaw.reduce((m, inp) => {
        const idx = Number(inp?.index ?? inp?.inputIndex);
        return Number.isFinite(idx) && idx > m ? idx : m;
      }, 0);
      const metaCount = Array.isArray(metaMppts)
        ? metaMppts[mpptIdx]
        : null;
      const inputsCount = Math.max(
        maxInputIdx,
        inputsRaw.length,
        Number.isFinite(Number(metaCount)) ? Number(metaCount) : 0
      );

      const modules =
        mppt?.modulesPerString != null
          ? mppt.modulesPerString
          : mppt?.modules_per_string != null
          ? mppt.modules_per_string
          : inputsRaw.find((inp) => inp?.modules_per_string != null)
              ?.modules_per_string ?? "";

      const hasIndexedInputs = inputsRaw.some((inp) =>
        Number.isFinite(Number(inp?.index ?? inp?.inputIndex))
      );

      const inputs = Array.from({ length: inputsCount || 0 }, (_, idx) => {
        const match = hasIndexedInputs
          ? inputsRaw.find(
              (inp) => Number(inp?.index ?? inp?.inputIndex) === idx + 1
            )
          : inputsRaw[idx];
        return {
          used: Boolean(match && connectedInput(match)),
          modules_per_string: match?.modules_per_string ?? match?.modulesPerString,
          inputIndex: Number(match?.index ?? match?.inputIndex) || idx + 1,
        };
      });

      return {
        ...mppt,
        modulesPerString: modules === undefined ? "" : String(modules),
        inputs,
      };
    });
  });

  return {
    nInverters: initialDesign.nInverters || normalizedLayout.length || 1,
    inverterOrder: inferredOrder,
    layout: normalizedLayout,
  };
}


/**
 * Props:
 *  - open
 *  - onClose()
 *  - inverterName
 *  - moduleName
 *  - meta: { name, mppts: [inputs_mppt1, inputs_mppt2, ...] }
 *  - initialDesign: previously saved designSummary (or undefined)
 *  - onSave(designSummary)
 */
function PVDesignDiagram({ inverterName, moduleName, mppts, parallelStrings = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const totalStrings = mppts.reduce(
    (sum, m) => sum + m.inputs.filter((i) => i.used).length,
    0
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        background: isDark
          ? "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.04))"
          : "linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0.06))",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          columnGap: 1.5,
          rowGap: 0.5,
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <DeviceHubIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>
            Stringing preview (active inverter)
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.7, whiteSpace: "nowrap" }}>
          Total strings: <strong>{totalStrings}</strong>
        </Typography>
      </Box>

      {/* Inverter block at the right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <Box
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            bgcolor: "background.paper",
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.25,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Inverter
          </Typography>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 180,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={inverterName}
          >
            {inverterName || "—"}
          </Typography>
        </Box>
      </Box>

      {/* MPPT rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {mppts.map((mppt, idx) => {
          const usedInputs = mppt.inputs.filter((i) => i.used).length;
          const totalInputs = mppt.inputs.length;
          const anyUsed = usedInputs > 0;

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Left: strings / inputs */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 120,
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <SolarPowerIcon
                    fontSize="small"
                    sx={{ opacity: anyUsed ? 1 : 0.4 }}
                  />
                  <Typography variant="caption">
                    MPPT #{idx + 1}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {mppt.inputs.map((inp, j) => parallelStrings && !inp.used ? null : (
                    <Box
                      key={j}
                      sx={{
                        px: 0.6,
                        py: 0.2,
                        borderRadius: 10,
                        border: "1px solid",
                        borderColor: inp.used
                          ? "success.main"
                          : "text.disabled",
                        bgcolor: inp.used
                          ? "success.light"
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        S{j + 1}
                      </Typography>
                      {inp.used && (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.65rem", opacity: 0.8 }}
                        >
                          {mppt.modulesPerString || "—"} mod
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Middle: line + MPPT block */}
              <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                {/* line from strings to MPPT */}
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    bgcolor: "text.disabled",
                  }}
                />

                {/* MPPT block */}
                <Box
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: anyUsed
                      ? "primary.light"
                      : isDark ? "rgba(255,255,255,0.07)" : "grey.200",
                    border: "1px solid",
                    borderColor: anyUsed
                      ? "primary.main"
                      : isDark ? "rgba(148,163,184,0.3)" : "grey.400",
                    minWidth: 90,
                    textAlign: "center",
                    mx: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    MPPT #{idx + 1}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {parallelStrings ? stringCountLabel(usedInputs) : `${usedInputs}/${totalInputs} ${totalInputs === 1 ? "input" : "inputs"}`}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {anyUsed
                      ? `${mppt.modulesPerString || "—"} mod / string`
                      : "No active strings"}
                  </Typography>
                </Box>

                {/* line from MPPT to inverter */}
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    bgcolor: "text.disabled",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Footer summary */}
      <Box sx={{ mt: "auto", pt: 1, borderTop: "1px dashed", borderColor: "divider" }}>
        <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
          Module: <strong>{moduleName || "—"}</strong>
        </Typography>
      </Box>
    </Paper>
  );
}



export default function DesignSystemDialog({
  inline = false,
  open,
  onClose,
  onDiscard,
  onDownloadReport,
  inverterName,
  moduleName,
  meta,
  showAutoDesignBanner,
  showUnsavedMarker,
  initialDesign,
  designCapacityKw,
  moduleKw,
  downloadReportLoading = false,
  canDownloadReport = false,
  reportSaved = null,
  reportRetentionOk = null,
  reportHistory = [],
  savedReportId = null,
  reportHistoryLoading = false,
  reportHistoryError = false,
  onValidate = null,
  validationKey = "",
  onLoadValidationContext = null,
  designSettings = null,
  onSave,
  // Told whenever the strings differ from what is saved.
  onDirtyChange = null,
  // The plant's orientations (config/orientations.orientationsFromConfig).
  // With more than one, every MPPT gets a plane to stand on; with one, any
  // tag left in the design is cleared on save.
  orientations = null,
  // The quick study's «Δημιουργία και εκτέλεση»: a new value asks for one
  // save of the design on screen, through handleSave exactly as the button
  // does; onAutoSaveDone receives a status or a classified failure object.
  autoSaveRequest = 0,
  onAutoSaveDone = null,
}) {
  const theme = useTheme();
  const isGreek = useIsGreek();
  const tr = (el, en) => (isGreek ? el : en);
  const savedReport = savedReportId
    ? reportHistory.find((report) => report.report_id === savedReportId)
    : null;
  const otherReports = savedReport
    ? reportHistory.filter((report) => report.report_id !== savedReportId)
    : reportHistory;
  const reportDateLabel = (createdAt) => {
    const date = new Date(createdAt);
    return Number.isNaN(date.getTime())
      ? createdAt
      : date.toLocaleString("en-GB", {
          dateStyle: "short", timeStyle: "short",
        });
  };
  const planeList = Array.isArray(orientations) ? orientations : [];
  const multiPlane = planeList.length > 1;
  const primaryPlaneId = planeList[0]?.id || PRIMARY_ID;
  const planeIds = new Set(planeList.map((o) => o.id));
  // An MPPT's plane: its tag when the tag names a plane that exists, the
  // primary otherwise (untagged, or a plane that has since been removed).
  const planeOf = (mppt) =>
    mppt?.orientation && planeIds.has(mppt.orientation) ? mppt.orientation : primaryPlaneId;
  const planeLabel = (o, i) => `${i + 1} · ${displayName(o, isGreek)}`;
  const isDark = theme.palette.mode === "dark";
  const [nInverters, setNInverters] = useState(1);
  const [activeInv, setActiveInv] = useState(0);
  const [designPerInv, setDesignPerInv] = useState([]); // [ [mpptState...], [mpptState...] ]
  const [inverterOrder, setInverterOrder] = useState([]);
  const [didInitForThisOpen, setDidInitForThisOpen] = useState(false);
  const inverterTabsRef = useRef(null);
  const baselineRef = useRef("");
  const [showTabArrows, setShowTabArrows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savePhase, setSavePhase] = useState("checking");
  const [validation, setValidation] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [copyRequest, setCopyRequest] = useState(null);
  const [copyTargets, setCopyTargets] = useState(["new"]);
  const [copyConfirm, setCopyConfirm] = useState(false);
  const savePendingRef = useRef(false);
  const saveEpochRef = useRef(0);
  const liveDraftRef = useRef("");
  const feedbackRef = useRef(null);
  const [contextState, setContextState] = useState({ owner: null, status: "idle", data: null });
  const [contextRetry, setContextRetry] = useState(0);
  useEffect(() => {
    if (!open || typeof onLoadValidationContext !== "function") return undefined;
    let cancelled = false;
    setContextState({ owner: onLoadValidationContext, status: "loading", data: null });
    Promise.resolve().then(onLoadValidationContext).then((data) => {
      if (!isElectricalContext(data)) throw new Error("Incomplete electrical context");
      if (!cancelled) setContextState({ owner: onLoadValidationContext, status: "ready", data });
    }).catch(() => {
      if (!cancelled) setContextState({ owner: onLoadValidationContext, status: "failed", data: null });
    });
    return () => { cancelled = true; };
  }, [open, onLoadValidationContext, contextRetry]);
  const currentContext = contextState.owner === onLoadValidationContext ? contextState
    : { status: typeof onLoadValidationContext === "function" ? "loading" : "idle", data: null };
  const preview = liveElectricalDesign(designPerInv, currentContext.data, {
    dcacMin: designSettings?.dcacMin, dcacMax: designSettings?.dcacMax, orientations: planeList,
  }, isGreek);
  const activePreview = preview?.inverters[activeInv];
  const parallelStrings = ["parallel_strings", "combiner"].includes(meta?.connection_mode) || Boolean(currentContext.data?.inverter?.parallel_strings);
  const allSaveWarnings = (validation?.warnings || []).filter((issue) => !isDesignWorkflowNote(issue));
  // Operating-current exceedances the designer accepted: saving is allowed and
  // the simulation clips them. One line for all of them, details on demand,
  // instead of one orange bullet per MPPT of every inverter.
  const operatingSaveNotes = allSaveWarnings.filter((issue) => issue?.code === "operating_limit");
  const saveWarnings = allSaveWarnings.filter((issue) => issue?.code !== "operating_limit");
  liveDraftRef.current = JSON.stringify({ nInverters, designPerInv, inverterOrder, moduleName, inverterName, validationKey });

  useEffect(() => {
    saveEpochRef.current += 1;
    savePendingRef.current = false;
    setSaving(false);
    setValidation(null);
    setSaveError("");
    return () => { saveEpochRef.current += 1; };
  }, [open, moduleName, inverterName, validationKey]);

  useEffect(() => {
    if (saveError || validation?.errors?.length || validation?.warnings?.some((issue) => !isDesignWorkflowNote(issue))) {
      feedbackRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    }
  }, [saveError, validation]);

  // Reset the "didInit" flag when dialog closes
  useEffect(() => {
    if (!open) {
      setDidInitForThisOpen(false);
      return;
    }
    const { nInverters: nInv, layout, inverterOrder: order } =
      buildLayoutFromInitialDesign(initialDesign, meta);
    setNInverters(nInv);
    setDesignPerInv(layout);
    setInverterOrder(order || []);
    setActiveInv(0);
    setCopyRequest(null);
    setDidInitForThisOpen(true);
    // The saved design as loaded, padded the way the order effect below pads
    // it, so the comparison is against what the screen will actually show.
    const padded = [...(order || [])];
    while (padded.length < nInv) padded.push(`INVERTER #${padded.length + 1}`);
    baselineRef.current = JSON.stringify({ n: nInv, layout, order: padded.slice(0, nInv) });
  }, [open, meta, initialDesign]);

  useEffect(() => {
    if (!didInitForThisOpen || typeof onDirtyChange !== "function") return;
    const now = JSON.stringify({ n: nInverters, layout: designPerInv, order: inverterOrder.slice(0, nInverters) });
    onDirtyChange(now !== baselineRef.current);
  }, [didInitForThisOpen, nInverters, designPerInv, inverterOrder, onDirtyChange]);

  // 🟦 STEP 2: keep array length in sync
  useEffect(() => {
    if (!open || !didInitForThisOpen) return;

    setInverterOrder((prev) => {
      const next = [...prev];
      let changed = false;
      while (next.length < nInverters) {
        next.push(`INVERTER #${next.length + 1}`);
        changed = true;
      }
      if (next.length > nInverters) {
        next.length = nInverters;
        changed = true;
      }
      return changed ? next : prev;
    });

    setDesignPerInv((prev) => {
      const next = [...prev];
      let changed = false;

      while (next.length < nInverters) {
        const metaForInv =
          meta?.byInverter?.[inverterOrder[next.length]] || meta || null;
        const templateMppts =
          prev[0] ||
          (Array.isArray(initialDesign?.layout) ? initialDesign.layout[0] : null) ||
          (initialDesign?.layoutByInverter
            ? Object.values(initialDesign.layoutByInverter)[0]
            : null) ||
          [];
        const baseMppts = buildInitialMpptState(metaForInv, templateMppts);
        next.push(JSON.parse(JSON.stringify(baseMppts)));
        changed = true;
      }
      if (next.length > nInverters) {
        next.length = nInverters;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [open, didInitForThisOpen, nInverters, meta, inverterOrder, initialDesign]);

  const mppts = designPerInv[activeInv] || [];
  const inferredCapacityKw = useMemo(() => {
    const parsedDesignCapacity =
      designCapacityKw != null && Number.isFinite(Number(designCapacityKw))
        ? Number(designCapacityKw)
        : null;

    const hasLiveLayout = Array.isArray(designPerInv) && designPerInv.length > 0;
    const baseLayout =
      hasLiveLayout && designPerInv
        ? designPerInv
        : Array.isArray(initialDesign?.layout)
        ? initialDesign.layout
        : initialDesign?.layoutByInverter
        ? Object.values(initialDesign.layoutByInverter)
        : null;

    const countModules = (layout) => {
      if (!Array.isArray(layout)) return 0;
      let modules = 0;
      layout.forEach((inv) => {
        (inv || []).forEach((mppt) => {
          const mps = Number(mppt?.modulesPerString) || 0;
          const used = (mppt?.inputs || []).filter((i) => i?.used).length;
          modules += mps * used;
        });
      });
      return modules;
    };

    if (baseLayout) {
      const modules = countModules(baseLayout);
      if (moduleKw != null && Number.isFinite(Number(moduleKw))) {
        const kw = modules * Number(moduleKw);
        if (Number.isFinite(kw)) return kw;
      }

      // fallback: scale from initial capacity using module count ratio
      const initialModules = countModules(
        Array.isArray(initialDesign?.layout)
          ? initialDesign.layout
          : initialDesign?.layoutByInverter
          ? Object.values(initialDesign.layoutByInverter)
          : null
      );
      if (parsedDesignCapacity != null && initialModules > 0) {
        const scaled = parsedDesignCapacity * (modules / initialModules);
        if (Number.isFinite(scaled)) return scaled;
      }
    }

    return parsedDesignCapacity;
  }, [designCapacityKw, designPerInv, initialDesign, moduleKw]);

  // Modules and strings across the whole design. The tab showed each
  // inverter's strings and a capacity in kW, but never the module count the
  // layout, the parcel fit and the purchase order are all written in.
  const designTotals = useMemo(() => {
    let modules = 0;
    let strings = 0;
    (Array.isArray(designPerInv) ? designPerInv : []).forEach((inv) => {
      (inv || []).forEach((mppt) => {
        const mps = Number(mppt?.modulesPerString) || 0;
        const used = (mppt?.inputs || []).filter((i) => i?.used).length;
        if (mps > 0) {
          strings += used;
          modules += mps * used;
        }
      });
    });
    return { modules, strings, inverters: Array.isArray(designPerInv) ? designPerInv.length : 0 };
  }, [designPerInv]);

  // The same totals per orientation, so a plane left without strings is seen
  // here, before the design is saved, rather than as a refused simulation.
  const planeTotals = (() => {
    if (!multiPlane) return [];
    const totals = Object.fromEntries(planeList.map((o) => [o.id, { id: o.id, modules: 0, strings: 0 }]));
    (Array.isArray(designPerInv) ? designPerInv : []).forEach((inv) => {
      (inv || []).forEach((mppt) => {
        const mps = Number(mppt?.modulesPerString) || 0;
        const used = (mppt?.inputs || []).filter((i) => i?.used).length;
        if (mps <= 0 || !used) return;
        const total = totals[planeOf(mppt)];
        total.modules += mps * used;
        total.strings += used;
      });
    });
    return planeList.map((o) => totals[o.id]);
  })();

  useEffect(() => {
    const node = inverterTabsRef.current;
    if (!node) {
      setShowTabArrows(false);
      return undefined;
    }

    const updateOverflow = () => {
      setShowTabArrows(node.scrollWidth > node.clientWidth + 4);
    };

    updateOverflow();
    window.addEventListener("resize", updateOverflow);

    return () => {
      window.removeEventListener("resize", updateOverflow);
    };
  }, [nInverters, open]);

  // One automatic save per request, once the design on screen is the one
  // just loaded. The timer lets the initialisation effects above settle, and
  // the ref makes it the latest handleSave (with the latest state) that runs.
  // handleSave is defined further down (after the loading return); the ref is
  // pointed at it there on every render.
  const handleSaveRef = useRef(null);
  const autoSaveDoneRef = useRef(0);
  useEffect(() => {
    if (!autoSaveRequest || autoSaveDoneRef.current === autoSaveRequest) return undefined;
    if (!open || !didInitForThisOpen || !designPerInv.length) return undefined;
    const timer = setTimeout(async () => {
      if (autoSaveDoneRef.current === autoSaveRequest) return;
      autoSaveDoneRef.current = autoSaveRequest;
      const result = handleSaveRef.current ? await handleSaveRef.current({ automatic: true }) : "error";
      if (typeof onAutoSaveDone === "function") onAutoSaveDone(result);
    }, 50);
    return () => clearTimeout(timer);
  }, [autoSaveRequest, open, didInitForThisOpen, designPerInv, onAutoSaveDone]);

  if (!designPerInv.length) {
    return (
      <PanelShell inline={inline} open={open} onClose={onClose} maxWidth="md">
        <PanelShellTitle inline={inline}>Design PV system</PanelShellTitle>
        <PanelShellContent inline={inline}>
          <PvLoadingRegion isGreek={isGreek} />
        </PanelShellContent>
        <PanelShellActions inline={inline}>
          {!inline && <Button onClick={onClose}>Close</Button>}
        </PanelShellActions>
      </PanelShell>
    );
  }

  // ------------ handlers for current inverter --------------

  const copyHasDesign = layout => (layout || []).some(m =>
    m.inputs?.some(input => input.used) || String(m.modulesPerString ?? "").trim() !== ""
  );
  const handleCopyInverter = () => {
    if (savePendingRef.current || !copyRequest) return;
    const targets = [...new Set(copyTargets.map(value => value === "new" ? nInverters : Number(value)))];
    if (!targets.length || targets.some(target => target === copyRequest.source || target < 0 || target >= 20 || target > nInverters)) return;
    if (targets.some(target => target < nInverters && copyHasDesign(designPerInv[target])) && !copyConfirm) {
      setCopyConfirm(true);
      return;
    }
    setDesignPerInv(prev => {
      const next = prev.slice(0, nInverters);
      targets.forEach(target => { next[target] = JSON.parse(JSON.stringify(copyRequest.layout)); });
      return next;
    });
    if (targets.includes(nInverters)) setNInverters(nInverters + 1);
    setActiveInv(targets[0]);
    setValidation(null);
    setSaveError("");
    setCopyRequest(null);
  };

  const updateActiveInverter = (updateFn) => {
    if (savePendingRef.current) return;
    setValidation(null);
    setSaveError("");
    setDesignPerInv((prev) =>
      prev.map((inv, invIdx) =>
        invIdx !== activeInv ? inv : updateFn(inv)
      )
    );
  };

  const handleToggleInput = (mpptIdx, inputIdx) => {
    updateActiveInverter((inv) =>
      inv.map((m, i) =>
        i !== mpptIdx
          ? m
          : {
              ...m,
              inputs: m.inputs.map((inp, j) =>
                j === inputIdx ? { ...inp, used: !inp.used } : inp
              ),
            }
      )
    );
  };

  const handleModulesPerStringChange = (mpptIdx, value) => {
    updateActiveInverter((inv) =>
      inv.map((m, i) =>
        i !== mpptIdx ? m : { ...m, modulesPerString: value }
      )
    );
  };

  const handleAddParallelString = (mpptIdx) => {
    updateActiveInverter(inv => inv.map((mppt, index) => {
      if (index !== mpptIdx) return mppt;
      const free = mppt.inputs.findIndex(input => !input.used);
      return { ...mppt, inputs: free < 0
        ? [...mppt.inputs, { used: true, inputIndex: mppt.inputs.length + 1 }]
        : mppt.inputs.map((input, i) => i === free ? { ...input, used: true } : input) };
    }));
  };

  const handleClearMppt = (mpptIdx) => {
    updateActiveInverter((inv) => {
      const next = inv.map((m, i) =>
        i !== mpptIdx
          ? m
          : {
              ...m,
              modulesPerString: "",
              inputs: m.inputs.map((inp) => ({ ...inp, used: false })),
            }
      );
      const hardwareCount = currentContext.data?.ready ? currentContext.data.mppts.length : null;
      if (hardwareCount != null) {
        while (next.length > hardwareCount && !next[next.length - 1].inputs.some(input => input.used)) next.pop();
      }
      return next;
    });
  };

  const handleMpptOrientation = (mpptIdx, orientationId) => {
    updateActiveInverter((inv) =>
      inv.map((m, i) => (i !== mpptIdx ? m : withMpptOrientation(m, orientationId, primaryPlaneId)))
    );
  };

  const handleSave = async ({ automatic = false } = {}) => {
    if (savePendingRef.current) return "busy";
    const order =
      inverterOrder && inverterOrder.length
        ? inverterOrder.slice(0, nInverters)
        : Array.from({ length: nInverters }, (_, i) => `INVERTER #${i + 1}`);

    // Tags are kept only where they name a further plane that exists: a tag
    // for a removed plane, or any tag on a single-plane plant, is cleared, so
    // the save cannot carry a reference the engine would refuse.
    const layout = Array.isArray(orientations)
      ? designPerInv.map((inv) =>
          (inv || []).map((m) => withMpptOrientation(m, planeOf(m), primaryPlaneId))
        )
      : designPerInv;

    const layoutByInverter = {};
    order.forEach((name, idx) => {
      layoutByInverter[name] = layout[idx] || [];
    });

    const designSummary = {
      module: moduleName,
      inverter: inverterName,
      nInverters,
      inverterOrder: order,
      layoutByInverter,
      layout,   // ALL inverters, ALL MPPTs
    };

    if (inferredCapacityKw != null && Number.isFinite(Number(inferredCapacityKw))) {
      designSummary.capacityKw = Number(inferredCapacityKw);
    }

    const snapshot = JSON.parse(JSON.stringify(designSummary));
    // Standalone consumers without a validation callback retain their contract.
    // ConfigForm supplies the backend check for every application save.
    if (typeof onValidate !== "function") {
      if (onSave) onSave(snapshot);
      return "saved";
    }
    const epoch = ++saveEpochRef.current;
    const draft = liveDraftRef.current;
    savePendingRef.current = true;
    setSaving(true);
    setSavePhase("checking");
    setValidation(null);
    setSaveError("");
    try {
      const checked = await onValidate(snapshot);
      if (epoch !== saveEpochRef.current) return "changed";
      if (draft !== liveDraftRef.current) {
        setSaveError(tr("Η σχεδίαση άλλαξε κατά τον έλεγχο. Ελέγξτε ξανά πριν από την αποθήκευση.", "The design changed during the check. Check it again before saving."));
        return "changed";
      }
      if (typeof checked?.valid !== "boolean" || !Array.isArray(checked.errors) || !Array.isArray(checked.warnings)
        || ![...checked.errors, ...checked.warnings].every((issue) => typeof issue?.message?.en === "string" && typeof issue?.message?.el === "string")) {
        throw electricalSaveError(tr("Ο ηλεκτρολογικός έλεγχος επέστρεψε ελλιπή απάντηση. Το σχέδιο δεν αποθηκεύτηκε· δοκιμάστε ξανά.", "The electrical check returned an incomplete response. The design was not saved; please try again."));
      }
      if (!checked.valid || checked.errors.length) {
        setValidation({ ...checked, valid: false });
        return "invalid";
      }
      setSavePhase("saving");
      if (onSave) await onSave(snapshot);
      if (epoch === saveEpochRef.current) setValidation(checked);
      return "saved";
    } catch (error) {
      const failure = electricalSaveFailure(error, isGreek);
      if (epoch === saveEpochRef.current && !(automatic && typeof onAutoSaveDone === "function")) {
        setSaveError(failure.message);
      }
      return failure;
    } finally {
      if (epoch === saveEpochRef.current) {
        savePendingRef.current = false;
        setSaving(false);
      }
    }
  };

  handleSaveRef.current = handleSave;

  const issueText = (issue) => {
    const location = [];
    if (issue.inverter) location.push(tr(`Αντιστροφέας ${issue.inverter}`, `Inverter ${issue.inverter}`));
    if (issue.mppt) location.push(`MPPT ${issue.mppt}`);
    const plane = planeList.findIndex((o) => o.id === issue.orientation);
    if (plane >= 0) location.push(planeLabel(planeList[plane], plane));
    const message = issue.message?.[isGreek ? "el" : "en"] || tr("Ελέγξτε τα στοιχεία της σχεδίασης.", "Review the design details.");
    return (location.length ? `${location.join(" · ")}: ` : "") + message;
  };

  const scrollInverterTabs = (direction) => {
    const node = inverterTabsRef.current;
    if (!node) return;
    node.scrollBy({
      left: direction * 240,
      behavior: "smooth",
    });
  };


  // ------------------------ render ------------------------

  return (
    <PanelShell inline={inline} open={open} onClose={onClose} maxWidth="lg">
      <PanelShellTitle inline={inline}>
        Design PV system{showUnsavedMarker ? " *" : ""}
      </PanelShellTitle>
      <PanelShellContent
        inline={inline}
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "0 0 auto",
            px: 3,
            pt: 2,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          {showAutoDesignBanner && (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                alignItems: "center",
                "& .MuiAlert-action": {
                  alignItems: "center",
                  pt: 0,
                  mr: 0,
                },
              }}
              action={
                showUnsavedMarker ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onDiscard}
                    disabled={saving}
                    sx={{
                      bgcolor: "error.main",
                      color: "#fff",
                      fontWeight: 700,
                      "&:hover": {
                        bgcolor: "error.dark",
                      },
                    }}
                  >
                    Discard
                  </Button>
                ) : null
              }
            >
              Auto-design results loaded. Review and click Save design to keep them.
            </Alert>
          )}
          {showAutoDesignBanner && multiPlane ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {tr(
                "Ο αυτόματος σχεδιασμός τοποθετεί όλα τα MPPT στον προσανατολισμό 1. Συνδέστε MPPT στους υπόλοιπους προσανατολισμούς παρακάτω πριν από την αποθήκευση.",
                "Auto-design places every MPPT on orientation 1. Attach MPPTs to the other orientations below before saving."
              )}
            </Alert>
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Module: <strong>{moduleName || "—"}</strong>
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Inverter: <strong>{inverterName || "—"}</strong>
              </Typography>
              {designTotals.modules > 0 && (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {designTotals.modules.toLocaleString(isGreek ? "el-GR" : "en-GB")} {designTotals.modules === 1 ? tr("πλαίσιο", "module") : tr("πλαίσια", "modules")} {tr("σε", "in")}{" "}
                  {designTotals.strings.toLocaleString(isGreek ? "el-GR" : "en-GB")} {designTotals.strings === 1 ? "string" : "strings"} {tr("σε", "across")}{" "}
                  {designTotals.inverters} {designTotals.inverters === 1 ? tr("αντιστροφέα", "inverter") : tr("αντιστροφείς", "inverters")}
                  {inferredCapacityKw != null
                    ? ` · ${Number(inferredCapacityKw).toLocaleString(isGreek ? "el-GR" : "en-GB", { maximumFractionDigits: 2 })} kWp ${tr("βάσει σχεδιασμού", "from the design")}`
                    : ""}
                </Typography>
              )}
              {multiPlane ? (
                <Box sx={{ mt: 0.5 }} data-testid="design-orientation-totals">
                  {planeTotals.map((t, i) => (
                    <Typography
                      key={t.id}
                      variant="body2"
                      sx={{ color: t.modules ? "text.secondary" : "warning.main" }}
                    >
                      {planeLabel(planeList[i], i)}:{" "}
                      {t.modules
                        ? isGreek
                          ? `${t.modules.toLocaleString("en-GB")} ${t.modules === 1 ? "πλαίσιο" : "πλαίσια"} σε ${t.strings.toLocaleString("en-GB")} ${t.strings === 1 ? "string" : "strings"}`
                          : `${t.modules.toLocaleString("en-GB")} ${t.modules === 1 ? "module" : "modules"} in ${t.strings.toLocaleString("en-GB")} ${t.strings === 1 ? "string" : "strings"}`
                        : tr("χωρίς συνδεδεμένο MPPT", "no MPPT attached")}
                    </Typography>
                  ))}
                </Box>
              ) : null}
              {designTotals.modules === 0 && inferredCapacityKw != null && (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {tr("Πραγματική ισχύς", "Actual capacity")}: {Number(inferredCapacityKw).toLocaleString(isGreek ? "el-GR" : "en-GB", { maximumFractionDigits: 2 })} kWp ({tr("βάσει σχεδιασμού", "from design")})
                </Typography>
              )}
            </Box>

            {onLoadValidationContext && <Box sx={{ flexBasis: "100%", width: "100%" }}>
              <LiveDesignStatus state={currentContext} preview={preview} greek={isGreek}
                onRetry={() => setContextRetry((n) => n + 1)} />
            </Box>}
            {canDownloadReport || reportSaved === false || reportHistory.length > 0 || reportHistoryLoading || reportHistoryError ? (
              <Box
                sx={{
                  flexBasis: "100%",
                  width: "100%",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  border: isDark
                    ? "1px solid rgba(100,116,139,0.3)"
                    : "1px solid rgba(96,165,250,0.28)",
                  bgcolor: isDark ? "rgba(30,41,59,0.7)" : "#f8fbff",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, mb: savedReport ? 0.6 : 0.3 }}>
                  {tr("Αναφορές αυτόματου σχεδιασμού", "Auto-design reports")}
                </Typography>
                {reportSaved === false && (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 0.6 }}>
                    {tr("Η νέα αναφορά δεν αποθηκεύτηκε· οι προηγούμενες παραμένουν διαθέσιμες.", "The new report was not saved; previous reports remain available.")}
                  </Typography>
                )}
                {reportRetentionOk === false && (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
                    {tr(
                      "Η διαγραφή παλαιότερων αναφορών δεν ολοκληρώθηκε.",
                      "Older reports could not be removed."
                    )}
                  </Typography>
                )}
                {reportHistoryLoading && (
                  <PvLoadingRegion minHeight={64} isGreek={isGreek} />
                )}
                {reportHistoryError && (
                  <Typography variant="caption" color="error.main" sx={{ display: "block", mb: 0.5 }}>
                    {tr("Το ιστορικό αναφορών δεν φορτώθηκε.", "Report history could not be loaded.")}
                  </Typography>
                )}
                {savedReport && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", px: 1, py: 0.65, mb: 0.5, border: "1px solid", borderColor: "primary.main", borderRadius: 1.5, bgcolor: isDark ? "rgba(37,99,235,0.14)" : "rgba(219,234,254,0.65)" }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
                        {tr("Αναφορά αποθηκευμένης σχεδίασης", "Saved design report")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {reportDateLabel(savedReport.created_at)}
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" disabled={downloadReportLoading} onClick={() => onDownloadReport(savedReport.report_id)}>
                      {tr("Λήψη κύριας αναφοράς", "Download primary report")}
                    </Button>
                  </Box>
                )}
                {savedReportId && !savedReport && !reportHistoryLoading && !reportHistoryError && (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
                    {tr("Η αναφορά της αποθηκευμένης σχεδίασης δεν βρέθηκε στο ιστορικό.", "The saved design's report was not found in the report history.")}
                  </Typography>
                )}
                {otherReports.length > 0 && (
                  <Box component="details" sx={{ mt: savedReport ? 0.5 : 0.25, "& summary": { cursor: "pointer", color: "primary.main", fontSize: "0.78rem", fontWeight: 600 } }}>
                    <Box component="summary">
                      {savedReport
                        ? tr(`Προηγούμενες αναφορές (${otherReports.length})`, `Other reports (${otherReports.length})`)
                        : tr(`Πρόσφατες αναφορές (${otherReports.length})`, `Recent reports (${otherReports.length})`)}
                    </Box>
                    {otherReports.map((report) => (
                        <Box key={report.report_id || "legacy"} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 0.4, borderTop: "1px solid", borderColor: "divider" }}>
                          <Typography variant="caption" sx={{ minWidth: 0 }}>
                            {reportDateLabel(report.created_at)}
                          </Typography>
                          <Button size="small" disabled={downloadReportLoading} onClick={() => onDownloadReport(report.report_id)}>
                            {tr("Λήψη", "Download")}
                          </Button>
                        </Box>
                    ))}
                  </Box>
                )}
                {canDownloadReport && reportHistory.length === 0 && <Button
                  variant="outlined"
                  size="small"
                  disabled={downloadReportLoading}
                  startIcon={downloadReportLoading ? <CircularProgress size={16} /> : null}
                  onClick={() => onDownloadReport()}
                >
                  {downloadReportLoading
                    ? tr("Λήψη...", "Downloading...")
                    : tr("Λήψη αναφοράς", "Download report")}
                </Button>}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
            <TextField
              label="Number of inverters"
              type="number"
              size="small"
              value={nInverters}
              disabled={saving}
              onChange={(e) => {
                if (savePendingRef.current) return;
                setValidation(null);
                setSaveError("");
                const val = Math.max(1, Math.min(20, Math.trunc(Number(e.target.value)) || 1));
                setNInverters(val);
                if (activeInv >= val) setActiveInv(val - 1);
              }}
              sx={{ width: 200, flexShrink: 0 }}
            />

            <Button size="small" sx={{ flexShrink: 0 }}
              disabled={saving || !copyHasDesign(mppts)}
              onClick={() => {
                setCopyRequest({ source: activeInv, layout: JSON.parse(JSON.stringify(mppts)) });
                setCopyTargets(nInverters === 1 ? ["new"] : []);
                setCopyConfirm(false);
              }}>
              {tr("Αντιγραφή σε…", "Copy to…")}
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: { xs: "1 1 100%", sm: 1 }, minWidth: 0 }}>
              {showTabArrows ? (
                <IconButton
                  size="small"
                  onClick={() => scrollInverterTabs(-1)}
                  sx={{ flex: "0 0 auto", border: "1px solid", borderColor: "divider" }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              ) : null}

              <Box
                ref={inverterTabsRef}
                sx={{
                  display: "flex",
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                  overflowX: "auto",
                  overflowY: "hidden",
                  flexWrap: "nowrap",
                  scrollBehavior: "smooth",
                  whiteSpace: "nowrap",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                {Array.from({ length: nInverters }, (_, i) => (
                  <Button
                    key={i}
                    size="small"
                    variant={activeInv === i ? "contained" : "outlined"}
                    color={preview?.inverters[i]?.issues.some((item) => item.severity === "error") ? "error" : "primary"}
                    onClick={() => setActiveInv(i)}
                    sx={{ flex: "0 0 auto" }}
                  >
                    {`INVERTER #${i + 1}`}{preview?.inverters[i]?.issues.some((item) => item.severity === "error") ? " !" : ""}
                  </Button>
                ))}
              </Box>

              {showTabArrows ? (
                <IconButton
                  size="small"
                  onClick={() => scrollInverterTabs(1)}
                  sx={{ flex: "0 0 auto", border: "1px solid", borderColor: "divider" }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 3,
            py: 2,
          }}
        >
          <Grid container spacing={2}>
          {/* LEFT: form / MPPT controls */}
          <Grid item xs={12} md={7}>
            {mppts.map((mppt, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: activePreview?.mppts[idx]?.issues.some((item) => item.severity === "error") ? "error.main" : "divider",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {parallelStrings ? `MPPT #${idx + 1} — ${stringCountLabel(mppt.inputs.filter(input => input.used).length)}`
                    : `MPPT #${idx + 1} — ${mppt.inputs.length} ${mppt.inputs.length === 1 ? "input" : "inputs"}`}
                </Typography>

                {parallelStrings && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {tr("Παράλληλα strings μέσω συνένωσης DC. ", "Parallel strings via DC combining. ")}
                  {activePreview?.mppts[idx]?.maxStrings != null && tr(
                    `Έως ${activePreview.mppts[idx].maxStrings} με το επιλεγμένο πλαίσιο και τις θερμοκρασίες σχεδιασμού· δεν είναι φυσικές είσοδοι DC.`,
                    `Up to ${activePreview.mppts[idx].maxStrings} with this module and design temperatures; these are not physical DC inputs.`)}
                </Typography>}

                <Grid container spacing={2} alignItems="center">
                  {/* Inputs */}
                  {mppt.inputs.map((inp, j) => parallelStrings && !inp.used ? null : (
                    <Grid item xs={12} sm={6} md={3} key={j}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={inp.used}
                            disabled={saving}
                            onChange={() => handleToggleInput(idx, j)}
                          />
                        }
                        label={parallelStrings ? `String ${j + 1}` : `Input ${j + 1}`}
                      />
                    </Grid>
                  ))}

                  {parallelStrings && <Grid item xs={12} sm={6} md={3}>
                    <Button size="small" variant="outlined" disabled={saving ||
                      activePreview?.mppts[idx]?.maxStrings == null ||
                      mppt.inputs.filter(input => input.used).length >= activePreview.mppts[idx].maxStrings}
                      onClick={() => handleAddParallelString(idx)}>+ String</Button>
                  </Grid>}

                  {/* Modules per string, disabled if no input used */}
                  <Grid item xs={12} sm={6} md={3}>
                    {(() => {
                      const anyUsed = mppt.inputs.some((i) => i.used);
                      return (
                        <TextField
                          fullWidth
                          size="small"
                          label="Modules/string"
                          type="number"
                          value={mppt.modulesPerString}
                          error={Boolean(activePreview?.mppts[idx]?.issues.some((i) => i.field === "length"))}
                          onChange={(e) =>
                            handleModulesPerStringChange(idx, e.target.value)
                          }
                          helperText={
                            anyUsed && activePreview?.mppts[idx]?.lengthHint
                              ? activePreview.mppts[idx].lengthHint
                              : anyUsed
                              ? "All used inputs in this MPPT share this value"
                              : parallelStrings ? tr("Προσθέστε τουλάχιστον ένα string.", "Add at least one string.") : "Enable at least one input above"
                          }
                          disabled={saving || !anyUsed}
                        />
                      );
                    })()}
                  </Grid>

                  {multiPlane ? (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={tr("Προσανατολισμός", "Orientation")}
                        value={planeOf(mppt)}
                        disabled={saving}
                        onChange={(e) => handleMpptOrientation(idx, e.target.value)}
                      >
                        {planeList.map((o, i) => (
                          <MenuItem key={o.id} value={o.id}>{planeLabel(o, i)}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : null}

                  <Grid item xs={12} sm={6} md={3}>
                    <Tooltip describeChild title={tr("Αποεπιλογή όλων των εισόδων αυτού του MPPT και καθαρισμός του αριθμού πλαισίων ανά string.", "Deselect all inputs of this MPPT and clear the modules-per-string value.")} arrow>
                      <Button
                        variant="text"
                        size="small"
                        aria-label={tr("Καθαρισμός συνδέσεων", "Clear connections")}
                        sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.75rem", textTransform: "none" }}
                        onClick={() => handleClearMppt(idx)}
                        disabled={saving}
                      >
                        {tr("Καθαρισμός", "Clear")}
                      </Button>
                    </Tooltip>
                  </Grid>
                </Grid>
                <LiveMpptFeedback row={activePreview?.mppts[idx]} greek={isGreek} />
              </Box>
            ))}
          </Grid>

          {/* RIGHT: diagram */}
          <Grid item xs={12} md={5}>
            {activePreview?.issues.some((i) => !i.mppt && !isDesignWorkflowNote(i)) && <Box sx={{ mb: 1.5 }} aria-live="polite">
              {activePreview.issues.filter((i) => !i.mppt && !isDesignWorkflowNote(i)).map((i, index) => <Typography key={index}
                variant="caption" component="p" color={`${i.severity}.main`} sx={{ my: 0.5 }}>{i.text}</Typography>)}
            </Box>}
            <PVDesignDiagram
              inverterName={inverterName}
              moduleName={moduleName}
              mppts={mppts}
              parallelStrings={parallelStrings}
            />
          </Grid>
          </Grid>
        </Box>

        <Box ref={feedbackRef} sx={{ px: 3 }} aria-live="polite">
          {saveError && <Alert severity="error">{saveError}</Alert>}
          {validation && (
            <>
            <Alert severity={!validation.valid ? "error" : saveWarnings.length ? "warning" : "success"}>
              {validation.valid
                ? saveWarnings.length
                  ? tr("Το σχέδιο αποθηκεύτηκε με προειδοποιήσεις. Ελέγξτε τα παρακάτω στοιχεία.", "The design was saved with warnings. Review the following items.")
                  : tr("Το σχέδιο αποθηκεύτηκε.", "The design was saved.")
                : tr("Το σχέδιο δεν αποθηκεύτηκε. Διορθώστε τα παρακάτω προβλήματα.", "The design was not saved. Correct the following issues.")}
              {(validation.errors.length > 0 || saveWarnings.length > 0) && (
                <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                  {(validation.valid ? saveWarnings : validation.errors).map((issue, index) => <li key={index}>{issueText(issue)}</li>)}
                </Box>
              )}
              {validation.valid && operatingSaveNotes.length > 0 && (
                <Box component="details" sx={{ mt: 0.75, "& > summary": { cursor: "pointer", color: "text.secondary", fontSize: "0.8125rem" } }}>
                  <Box component="summary">{tr(
                    `Το ρεύμα λειτουργίας ξεπερνά το όριο του αντιστροφέα σε ${operatingSaveNotes.length} έλεγχ${operatingSaveNotes.length === 1 ? "ο" : "ους"}· η προσομοίωση υπολογίζει την περικοπή.`,
                    `Operating current exceeds the inverter's limit in ${operatingSaveNotes.length} check${operatingSaveNotes.length === 1 ? "" : "s"}; the simulation calculates the clipping.`)}</Box>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, color: "text.secondary", fontSize: "0.8125rem" }}>
                    {operatingSaveNotes.map((issue, index) => <li key={index}>{issueText(issue)}</li>)}
                  </Box>
                </Box>
              )}
            </Alert>
            {!validation.valid && saveWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {tr("Πρόσθετες προειδοποιήσεις προς έλεγχο:", "Additional warnings to review:")}
                <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                  {saveWarnings.map((issue, index) => <li key={index}>{issueText(issue)}</li>)}
                </Box>
              </Alert>
            )}
            </>
          )}
        </Box>
      </PanelShellContent>

      <Dialog open={Boolean(copyRequest) && open} onClose={() => setCopyRequest(null)} fullWidth maxWidth="xs">
        <DialogTitle>{tr("Αντιγραφή σχεδίασης αντιστροφέα", "Copy inverter design")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {tr(`Αντιγραφή από τον αντιστροφέα ${Number(copyRequest?.source) + 1}: MPPT, είσοδοι, μήκη strings και προσανατολισμοί.`,
              `Copy from inverter ${Number(copyRequest?.source) + 1}: MPPTs, inputs, string lengths and orientations.`)}
          </Typography>
          <Typography variant="body2">{tr("Επιλέξτε έναν ή περισσότερους προορισμούς:", "Select one or more destinations:")}</Typography>
          <Button size="small" onClick={() => {
            setCopyTargets(Array.from({ length: nInverters }, (_, i) => String(i)).filter(value => Number(value) !== copyRequest?.source));
            setCopyConfirm(false);
          }} disabled={nInverters < 2}>{tr("Όλοι οι υπόλοιποι", "All other inverters")}</Button>
          <Box sx={{ display: "flex", flexDirection: "column", maxHeight: 280, overflowY: "auto" }}>
            {[...Array.from({ length: nInverters }, (_, i) => String(i)).filter(value => Number(value) !== copyRequest?.source), ...(nInverters < 20 ? ["new"] : [])].map(value => (
              <FormControlLabel key={value}
                label={value === "new" ? tr("Νέος αντιστροφέας", "New inverter") : tr(`Αντιστροφέας ${Number(value) + 1}`, `Inverter ${Number(value) + 1}`)}
                control={<Checkbox checked={copyTargets.includes(value)} onChange={e => {
                  setCopyTargets(prev => e.target.checked ? [...prev, value] : prev.filter(item => item !== value));
                  setCopyConfirm(false);
                }} />} />
            ))}
          </Box>
          {copyConfirm && <Alert severity="warning" sx={{ mt: 2 }}>
            {tr("Θα αντικατασταθεί η υπάρχουσα σχεδίαση στους αντιστροφείς:", "Existing designs will be replaced on inverters:")}
            {" "}{copyTargets.filter(value => value !== "new" && copyHasDesign(designPerInv[Number(value)])).map(value => Number(value) + 1).join(", ")}
          </Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyRequest(null)}>{tr("Ακύρωση", "Cancel")}</Button>
          <Button variant="contained" onClick={handleCopyInverter} disabled={saving || !copyTargets.length}>
            {copyConfirm ? tr("Αντικατάσταση σχεδίασης", "Replace design") : tr("Αντιγραφή", "Copy")}
          </Button>
        </DialogActions>
      </Dialog>
      <PanelShellActions inline={inline}>
        {!inline && <Button size="small" onClick={onClose} disabled={saving}>Cancel</Button>}
        <Button size="small" onClick={handleSave} variant="contained" disabled={saving}
          aria-busy={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {saving ? savePhase === "checking" ? tr("Έλεγχος σχεδίασης…", "Checking design…") : tr("Αποθήκευση…", "Saving…") : tr("Αποθήκευση σχεδίου", "Save design")}
        </Button>
      </PanelShellActions>
    </PanelShell>
  );
}
